import { computed, helpers, NetbankingAPI, ui } from "aaa";
import * as FileSaver from "file-saver";
import { action, observable } from "mobx";
import {
    EcosAPIClient,
    GenericAPIClient,
    NetbankingAPIClient,
} from "network/APIClient";
import { AccountsStore } from "stores/AccountsStore";
import { ContactsStore } from "stores/ContactsStore";
import { JuniorOffersStore } from "stores/JuniorOffersStore";
import {
    isJuniorGiro,
    isJuniorSmartSparen,
    isJuniorSpark7,
} from "stores/OffersStore";
import { ProfileStore } from "stores/ProfileStore";
import { pushRoute } from "stores/RouterFunctions";
import trackingStore from "stores/TrackingStore";
import { IAccount } from "types/account";
import { IOffer, IOfferConditionsResponse } from "types/offer";
import {
    IJuniorActivationCodeResponse,
    IJuniorActivationResponse,
    IJuniorContractResponse,
    IJuniorUser,
} from "types/profile";
import { b64ToBlob } from "util/helpers";
import { JuniorRoutes } from "./JuniorRoutes";

export const SMART_SPAREN_PRODUCT_CODE = "G-SMART-K";
export const SPARK7_PRODUCT_CODE = "G-ZVKOMFORT";

type Child = {
    id: string;
    name: string;
    active: boolean;
    age: number;
    dateOfBirth: Date;
    userNumber: number;
    customerId: string;
    gender: "MALE" | "FEMALE" | "UNKNOWN";
    juniorValue: "active" | "tooYoung" | "activationNeeded" | "migrationNeeded";
};

type Product = {
    id: string;
    name: string;
    isNew: boolean;
    description: string;
    productCode: string;
    minimumAge: number;
    available?: boolean; // Whether the product is available for the child
    returnCodes?: string[]; // Return codes from the API (e.g., "ACCOUNT_EXISTS")
    handover?: { url: string };
    channelProduct?: string;
    productId?: string;
    conditionsGroupId?: string;
    conditionsGroupCode?: string;
    marketingName?: string;
    accountType?: "CURRENT" | "SAVING";
    /** Smart Sparen offer tile in „Neues Produkt eröffnen“ — show „Empfohlen“ badge. */
    showRecommended?: boolean;
};

export class JuniorStore {
    @observable accountsStore?: AccountsStore;
    @observable profileStore?: ProfileStore;
    @observable juniorOffersStore?: JuniorOffersStore;
    @observable contactsStore?: ContactsStore;
    @observable _isLoading = false; // true;
    @observable gJuniorOffers?: IOffer[];

    @observable children: Child[] = [];

    @observable products: Product[] = [];
    @observable existingProducts: Product[] = [];

    @observable productConditions: Map<string, IOfferConditionsResponse> =
        new Map();

    @observable selectedChildId?: string;
    @observable productsLoadedForChildId?: string;

    @observable activationInfo?: IJuniorActivationResponse;
    @observable isLoadingActivation = false;
    @observable contractInfo?: IJuniorContractResponse;
    @observable isLoadingContract = false;
    @observable pdfData?: Uint8Array;
    @observable activationCodeResponse?: IJuniorActivationCodeResponse;
    @computed get selectedChild(): Child | undefined {
        return this.children.find((child) => child.id === this.selectedChildId);
    }

    @computed get availableProducts(): Product[] {
        return this.products.filter(
            (product) => product.productCode && product.conditionsGroupCode,
        );
    }

    @computed get mainContactEmail(): string {
        return this.contactsStore?.selectedEmailAddress?.email?.email || "";
    }

    // Product handover properties
    @observable smartSparenOffer?: IOffer;
    @observable spark7Offer?: IOffer;

    constructor() {
        this.profileStore = new ProfileStore();
        this.juniorOffersStore = new JuniorOffersStore(undefined);
        this.contactsStore = new ContactsStore();
    }

    @computed get isLoading() {
        return this._isLoading;
    }

    private getAge(birthDate: Date): number {
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
            age--;
        }

        return age;
    }

    private convertJuniorUsersToChildren(juniorUsers: IJuniorUser[]): Child[] {
        return juniorUsers.map((user) => {
            const birthDate =
                user.dateOfBirth instanceof Date
                    ? user.dateOfBirth
                    : new Date(user.dateOfBirth);

            return {
                id: user.customerId,
                name: user.firstName + " " + user.lastName,
                active: user.junior,
                age: this.getAge(birthDate),
                dateOfBirth: birthDate,
                userNumber: user.userNumber,
                customerId: user.customerId,
                gender: user.gender,
                juniorValue: user.juniorValue,
            };
        });
    }

    private convertOffersToProducts(offers?: IOffer[]): Product[] {
        if (!offers) {
            return [];
        }

        return offers.map((offer) => {
            const isNew = isJuniorSpark7(offer.channelProduct); // Spark 7 is new
            const minimumAge = isJuniorSmartSparen(offer.channelProduct)
                ? 5
                : 10; // Smart Sparen: 5+, Spark 7: 10+

            const smartSparen = isJuniorSmartSparen(offer.channelProduct);
            return {
                id: offer.productId,
                name: offer.marketingName || offer.productName,
                isNew,
                description: smartSparen
                    ? ui.__("gJunior.product.smartSparen.description")
                    : ui.__("gJunior.product.spark7.description"),
                productCode: smartSparen
                    ? SMART_SPAREN_PRODUCT_CODE
                    : SPARK7_PRODUCT_CODE,
                minimumAge,
                channelProduct: offer.channelProduct,
                productId: offer.productId,
                conditionsGroupId: offer.conditionsGroupId,
                conditionsGroupCode: offer.conditionsGroupCode,
                marketingName: offer.marketingName,
                handover: offer.handover,
                showRecommended: smartSparen,
            };
        });
    }

    /** Prefer API product label; otherwise `fallback` (i18n or fixed default). */
    private resolveExistingJuniorProductName(
        account: IAccount,
        fallback: string,
    ): string {
        return (
            account.productI18N || account.typeI18N || account.alias || fallback
        );
    }

    private convertAccountsToProducts(accounts?: IAccount[]): Product[] {
        if (!accounts) {
            return [];
        }

        const products: Product[] = [];
        const spark7NameFallback = "spark7 Jugendkonto";
        const otherSavingNameFallback = ui.__(
            "gJunior.product.juniorOtherSaving.fallbackName",
        );

        for (const account of accounts) {
            if (account.type === "CURRENT") {
                products.push({
                    id: account.id,
                    name: this.resolveExistingJuniorProductName(
                        account,
                        spark7NameFallback,
                    ),
                    isNew: true,
                    description: ui.__("gJunior.product.spark7.description"),
                    productCode: SPARK7_PRODUCT_CODE,
                    minimumAge: 10,
                    channelProduct: "GIROSPARK7",
                    accountType: "CURRENT",
                });
                continue;
            }

            if (account.type !== "SAVING") {
                continue;
            }

            const isSmartSparen = account.subType === "SMART_SPAREN";
            const isSmartSparenJugend = (this.selectedChild?.age ?? 0) >= 10;
            const smartSparenNameFallback = isSmartSparenJugend
                ? "Smart Sparen Jugend"
                : "Smart Sparen Sparefroh";

            if (isSmartSparen) {
                products.push({
                    id: account.id,
                    name: this.resolveExistingJuniorProductName(
                        account,
                        smartSparenNameFallback,
                    ),
                    isNew: false,
                    description: ui.__(
                        "gJunior.product.smartSparen.description",
                    ),
                    productCode: SMART_SPAREN_PRODUCT_CODE,
                    minimumAge: 5,
                    channelProduct: "SMARTJUNIOR",
                    accountType: "SAVING",
                });
                continue;
            }

            products.push({
                id: account.id,
                name: this.resolveExistingJuniorProductName(
                    account,
                    otherSavingNameFallback,
                ),
                isNew: false,
                description: ui.__(
                    "gJunior.product.juniorOtherSaving.description",
                ),
                productCode: SMART_SPAREN_PRODUCT_CODE,
                minimumAge: 5,
                accountType: "SAVING",
            });
        }

        return products;
    }

    @action async loadData() {
        try {
            this._isLoading = true;

            await this.profileStore?.load();

            const profile = this.profileStore?.profile;
            console.log("[JuniorStore] loadData profile:", profile);
            if (!profile) {
                return;
            }

            const juniorUsers = await NetbankingAPIClient.requestType(
                NetbankingAPI.getJuniorUsers(),
            );
            console.log("[JuniorStore] loadData juniorUsers:", juniorUsers);

            if (juniorUsers.juniorUsers && juniorUsers.juniorUsers.length > 0) {
                this.children = this.convertJuniorUsersToChildren(
                    juniorUsers.juniorUsers,
                );
            }

            this._isLoading = false;
        } catch (error) {
            console.error("Error loading junior users:", error);
        }
    }

    @action async selectChild(id: string) {
        this.selectedChildId = id;
        this.productsLoadedForChildId = undefined; // Reset the flag when selecting a new child
        this.productConditions.clear(); // Clear conditions when switching children
        this.juniorOffersStore?.setChildId(id); // Set the childId in the JuniorOffersStore
        await this.loadProductsForSelectedChild();
    }

    @action async loadProductsForSelectedChild() {
        if (!this.selectedChild) {
            this.products = [];
            this.productConditions.clear();
            this.productsLoadedForChildId = undefined;
            return;
        }

        if (this.productsLoadedForChildId === this.selectedChildId) {
            return;
        }

        try {
            this._isLoading = true;

            // Load offers for the selected child
            await this.juniorOffersStore?.load();
            console.log(
                "[JuniorStore] loadProductsForSelectedChild offers:",
                this.juniorOffersStore?.offers,
            );
            this.gJuniorOffers = this.juniorOffersStore?.juniorOffers;

            this.existingProducts = this.convertAccountsToProducts(
                this.juniorOffersStore?.filteredJuniorAccounts,
            );

            // Convert offers to products for display
            this.products = this.convertOffersToProducts(this.gJuniorOffers);

            // Load conditions for available products
            await this.loadProductConditions();

            // Track which products are show to the child
            this.trackProductsShownToChild();

            this._isLoading = false;
        } catch (error) {
            console.error("Error loading products for selected child:", error);
            this.products = [];
            this.gJuniorOffers = [];
            this._isLoading = false;
        } finally {
            // Always mark products as loaded for this child, even if no products were found or an error occurred
            this.productsLoadedForChildId = this.selectedChildId;
        }
    }

    private trackProductsShownToChild = () => {
        if (!this.availableProducts?.length) {
            return;
        }
        const productCode1 = this.availableProducts[0]?.productCode;
        const productCode2 = this.availableProducts[1]?.productCode;
        const productCode3 = this.availableProducts[2]?.productCode;
        // Track which products are show to the child
        trackingStore.trackEvent({
            eventName: "productsShownToChild",
            productKey: "FAMILYCORNER",
            data: {
                detailsKey1: "childCustomerId",
                detailsValue1: this.selectedChild?.customerId,
                ...(productCode1 && {
                    detailsKey2: "productCode1",
                    detailsValue2: productCode1,
                }),
                ...(productCode2 && {
                    detailsKey3: "productCode2",
                    detailsValue3: productCode2,
                }),
                ...(productCode3 && {
                    detailsKey4: "productCode3",
                    detailsValue4: productCode3,
                }),
            },
        });
    };

    @action async loadProductConditions() {
        if (!this.selectedChild) {
            return;
        }

        // Load conditions for each available product
        const productConditions = this.availableProducts.map(
            async (product) => {
                try {
                    if (!product.productCode || !product.conditionsGroupCode) {
                        return;
                    }

                    const target = NetbankingAPI.getProductConditions(
                        product.productCode,
                        product.conditionsGroupCode,
                    );
                    const conditions = await EcosAPIClient.requestType(target);

                    if (product.productId && conditions) {
                        this.productConditions.set(
                            product.productId,
                            conditions,
                        );
                    }
                } catch (error) {
                    console.error(
                        `Error loading conditions for product ${product.productId}:`,
                        error,
                    );
                }
            },
        );

        await Promise.all(productConditions);
    }

    // Helper method to get condition group based on age for Smart Sparen
    getSmartSparenConditionGroup(age: number): string {
        if (age >= 0 && age < 10) {
            return "SMARTKINDER";
        } else if (age >= 10 && age < 14) {
            return "SMARTJUGEND";
        }
        return "SMARTKINDER";
    }

    // Helper method to get institute URL path based on institute ID
    getInstitutePath(instituteId: number): string {
        // Map institute IDs to URL paths
        const instituteMap: { [key: number]: string } = {
            198: "erstebank", // Erste Bank
            // Add more mappings as needed
        };
        return instituteMap[instituteId] || "erstebank";
    }

    // Helper method to get product details path for information pages
    getProductDetailsPath(product: Product): string {
        if (isJuniorSmartSparen(product.channelProduct || "")) {
            // Smart Sparen - determine age group
            const conditionGroup = this.getSmartSparenConditionGroup(
                this.selectedChild?.age ?? 0,
            );

            if (conditionGroup === "SMARTKINDER") {
                return "privatkunden/sparen-anlegen/sparen/klassisches-sparen/smart-sparen-sparefroh";
            } else {
                return "privatkunden/sparen-anlegen/sparen/klassisches-sparen/smart-sparen-jugend";
            }
        } else if (
            isJuniorSpark7(product.channelProduct || "") ||
            isJuniorGiro(product.channelProduct || "")
        ) {
            return "spark7";
        }
        return "";
    }

    getHandoverUrl(productId: string): string | undefined {
        const product = this.products.find((p) => p.productId === productId);
        if (!product || !product.handover?.url) {
            return undefined;
        }

        if (product.handover?.url) {
            return product.handover.url;
        }
    }

    // Get product details URL for information pages
    getProductDetailsUrl(productId: string): string | undefined {
        const product = this.products.find((p) => p.productId === productId);
        if (!product || !this.selectedChild) {
            return undefined;
        }

        const instituteId = this.profileStore?.profile?.instituteId || 198;
        const institutePath = this.getInstitutePath(instituteId);
        const baseUrl = "https://www.sparkasse.at";
        const productPath = this.getProductDetailsPath(product);

        if (!productPath) {
            return undefined;
        }

        return `${baseUrl}/${institutePath}/${productPath}`;
    }

    // Get fee information URL from product conditions
    getFeeInformationUrl(productId: string): string | undefined {
        const conditions = this.productConditions.get(productId);
        if (!conditions) {
            return undefined;
        }

        return conditions.chargingInformation?.documentUrlView?.paymentInfoUrl;
    }

    @action async activateJunior() {
        if (!this.selectedChild) {
            throw new Error("No child selected");
        }

        const target = NetbankingAPI.activateJunior({
            juniorUserNumber: this.selectedChild.userNumber,
        });

        try {
            this.isLoadingActivation = true;
            this.activationInfo = await NetbankingAPIClient.requestType(target);
        } catch (error) {
            throw error;
        } finally {
            this.isLoadingActivation = false;
        }
    }

    @action async loadContacts() {
        if (!this.contactsStore) {
            return;
        }

        if (
            this.contactsStore.contacts.length === 0 &&
            !this.contactsStore.isLoading
        ) {
            try {
                await this.contactsStore.load();
            } catch (error) {
                console.error("Error loading contacts:", error);
                pushRoute("/error");
                throw error;
            }
        }
    }

    @action async createJuniorContract(parentInformationEmail: string) {
        if (!this.selectedChild) {
            throw new Error("No child selected");
        }

        const target = NetbankingAPI.createJuniorContract({
            juniorUserNumber: this.selectedChild.userNumber,
            parentInformationEmail: parentInformationEmail,
        });

        try {
            this.isLoadingContract = true;
            this.contractInfo = await NetbankingAPIClient.requestType(target);
        } catch (error) {
            pushRoute("/error");
        } finally {
            this.isLoadingContract = false;
        }
    }

    @action async loadPdfFromUrl(url: string) {
        try {
            this.isLoadingContract = true;
            const response = await GenericAPIClient.request({
                url: url.replace("https://", ""),
                method: "GET",
                headers: {
                    Accept: "application/pdf",
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            });

            if (helpers.isAndroid() || helpers.isIos()) {
                const arrayBuffer = await response.arrayBuffer();
                this.pdfData = new Uint8Array(arrayBuffer);
                pushRoute(JuniorRoutes.PDF);
            } else {
                const contentDispositionHeader = response.headers.get(
                    "content-disposition",
                );
                const contentDispositionParts = contentDispositionHeader
                    ?.split(";")
                    .map((part) => part.trim());
                const filenamePart =
                    contentDispositionParts?.find((part) =>
                        part.startsWith("filename"),
                    ) ?? "";
                const rawFilename = filenamePart.split("=")[1] ?? "";
                const filename = rawFilename
                    .replace(/["']/g, "")
                    .replace(/[<>:"/\\|?*\x00-\x1F]/g, "")
                    .trim();

                //const blob = await response.blob();
                const blob = b64ToBlob(
                    await response.text(),
                    "application/pdf",
                );

                FileSaver.saveAs(blob, filename);
            }
        } catch (error) {
            console.error("Error loading PDF:", error);
            pushRoute("/error");
        } finally {
            this.isLoadingContract = false;
        }
    }

    // TODO: This is not used yet
    @action async loadJuniorActivationCode() {
        if (!this.selectedChild) {
            return;
        }

        let childUserNumber = this.selectedChild?.userNumber?.toString();

        const target = NetbankingAPI.postJuniorActivationCode({
            juniorUserNumber: parseInt(childUserNumber),
            oneTimePassword: "123456",
            username: "testuser",
        });
        this.activationCodeResponse = (await NetbankingAPIClient.requestType(
            target,
        )) as IJuniorActivationCodeResponse;
    }
}



// WEBPACK FOOTER //
// ./src/components/gJunior/JuniorStore.tsx